// src/components/Integrations/integrationFunctions.js

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const PROVIDERS = Object.freeze({
  BITLY: 'bitly',
  BIGQUERY: 'bigquery',
});

const CALLABLES = Object.freeze({
  // Org-level integrations
  GET_INTEGRATION_STATUS: 'getIntegrationStatus',
  DISCONNECT_INTEGRATION: 'disconnectIntegration',
  START_BITLY_OAUTH: 'startBitlyOAuth',
  LIST_INTEGRATION_FORMS: 'listIntegrationForms',

  // Bitly provider metrics
  // Enterprise rule:
  // - The browser never calls Bitly directly.
  // - The backend syncs visible/stale provider metrics and writes cached values
  //   back to formSubmissions.
  SYNC_BITLY_SUBMISSION_CLICKS: 'syncBitlySubmissionClicks',

  // BigQuery org-level lifecycle
  GET_BIGQUERY_ORG_INTEGRATION: 'getBigQueryOrgIntegration',
  INSTALL_BIGQUERY_ORG_INTEGRATION: 'installBigQueryOrgIntegration',
  DISABLE_BIGQUERY_ORG_INTEGRATION: 'disableBigQueryOrgIntegration',

  // BigQuery customer-owned destination lifecycle
  SAVE_BIGQUERY_CONNECTION_DRAFT: 'saveBigQueryConnectionDraft',
  VALIDATE_BIGQUERY_CONNECTION_DRAFT: 'validateBigQueryConnectionDraft',
  PUBLISH_BIGQUERY_CONNECTION_DRAFT: 'publishBigQueryConnectionDraft',
  DISCARD_BIGQUERY_CONNECTION_DRAFT: 'discardBigQueryConnectionDraft',

  // BigQuery org-level export policy lifecycle
  SAVE_BIGQUERY_ORG_POLICY_DRAFT: 'saveBigQueryOrgPolicyDraft',
  PUBLISH_BIGQUERY_ORG_POLICY_DRAFT: 'publishBigQueryOrgPolicyDraft',
  DISCARD_BIGQUERY_ORG_POLICY_DRAFT: 'discardBigQueryOrgPolicyDraft',

  // Generic form-level integration lifecycle
  GET_FORM_INTEGRATION_CONFIG: 'getFormIntegrationConfig',
  SAVE_FORM_INTEGRATION_CONFIG: 'saveFormIntegrationConfig',
  PUBLISH_FORM_INTEGRATION_CONFIG: 'publishFormIntegrationConfig',
  DISCARD_FORM_INTEGRATION_DRAFT: 'discardFormIntegrationDraft',

  // BigQuery-specific form-level lifecycle
  GET_BIGQUERY_FORM_CONFIG: 'getBigQueryFormConfig',
  SAVE_BIGQUERY_FORM_CONFIG: 'saveBigQueryFormConfig',
  PUBLISH_BIGQUERY_FORM_CONFIG: 'publishBigQueryFormConfig',
  DISCARD_BIGQUERY_FORM_DRAFT: 'discardBigQueryFormDraft',

  // BigQuery export queue diagnostics / repair
  GET_BIGQUERY_EXPORT_QUEUE_HEALTH: 'getBigQueryExportQueueHealth',
  REPAIR_BIGQUERY_EXPORT_QUEUE_RECORDS: 'repairBigQueryExportQueueRecords',
});

const MAX_QUEUE_HEALTH_LIMIT = 5000;
const DEFAULT_QUEUE_HEALTH_LIMIT = 1000;
const MAX_REPAIR_EVENT_IDS = 200;

const DEFAULT_INTEGRATION_FORMS_PAGE_SIZE = 100;
const MAX_INTEGRATION_FORMS_PAGE_SIZE = 250;

/**
 * Small frontend integration-call helper.
 *
 * Enterprise rule:
 * - This module is a typed-ish client boundary for callable functions.
 * - It does not decide export policy.
 * - It does not build warehouse schemas.
 * - It does not mutate Firestore directly.
 * - BigQuery connection, validation, queue, warehouse, and repair behavior
 *   remain backend-owned.
 */
async function callIntegrationFunction(callableName, payload, options = {}) {
  const {
    logLabel = callableName,
    logLevel = 'debug',
  } = options;

  const callable = httpsCallable(functions, callableName);
  const response = await callable(payload);
  const data = response?.data || null;

  const logPayload = {
    callableName,
    payloadSummary: summarizePayloadForLogs(payload),
    responseSummary: summarizeResponseForLogs(data),
  };

  if (logLevel === 'warn') {
    console.warn(`[integrationFunctions] ${logLabel}:`, logPayload);
  } else if (logLevel === 'log') {
    console.log(`[integrationFunctions] ${logLabel}:`, logPayload);
  } else {
    console.debug(`[integrationFunctions] ${logLabel}:`, logPayload);
  }

  return data;
}

function isPlainObject(value) {
  return Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);
}

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeOptionalString(value) {
  const normalized = normalizeString(value);
  return normalized || null;
}

function normalizeProvider(provider) {
  return normalizeString(provider).toLowerCase();
}

function requireString(value, fieldName) {
  const normalized = normalizeString(value);

  if (!normalized) {
    throw new Error(`[integrationFunctions] ${fieldName} is required.`);
  }

  return normalized;
}

function requireProvider(provider) {
  const normalized = normalizeProvider(provider);

  if (!normalized) {
    throw new Error('[integrationFunctions] provider is required.');
  }

  return normalized;
}

function requirePlainObject(value, fieldName) {
  if (!isPlainObject(value)) {
    throw new Error(
      `[integrationFunctions] ${fieldName} must be a plain object.`,
    );
  }

  return value;
}

function normalizeMode(mode) {
  const normalized = normalizeString(mode).toLowerCase();

  if (
    normalized === 'draft' ||
    normalized === 'published' ||
    normalized === 'auto'
  ) {
    return normalized;
  }

  return 'auto';
}

function normalizeQueueHealthLimit(limit) {
  const parsed = Number(limit);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_QUEUE_HEALTH_LIMIT;
  }

  return Math.min(Math.floor(parsed), MAX_QUEUE_HEALTH_LIMIT);
}

function normalizeIntegrationFormsPageSize(pageSize) {
  const parsed = Number(pageSize);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_INTEGRATION_FORMS_PAGE_SIZE;
  }

  return Math.min(
    Math.floor(parsed),
    MAX_INTEGRATION_FORMS_PAGE_SIZE,
  );
}

function normalizeEventIds(eventIds) {
  if (!Array.isArray(eventIds)) {
    return [];
  }

  return Array.from(
    new Set(
      eventIds
        .map((eventId) => normalizeString(eventId))
        .filter(Boolean),
    ),
  );
}

function normalizeSubmissionIds(submissionIds) {
  if (!Array.isArray(submissionIds)) {
    return [];
  }

  return Array.from(
    new Set(
      submissionIds
        .map((submissionId) => normalizeString(submissionId))
        .filter(Boolean)
        .filter((submissionId) => !submissionId.startsWith('local_')),
    ),
  );
}

function normalizeBigQueryDestination(destination) {
  const safeDestination = requirePlainObject(destination, 'destination');

  return {
    projectId: requireString(
      safeDestination.projectId,
      'destination.projectId',
    ),
    datasetId: requireString(
      safeDestination.datasetId,
      'destination.datasetId',
    ),
    location: normalizeOptionalString(safeDestination.location) || 'US',
    tablePrefix:
      normalizeOptionalString(safeDestination.tablePrefix) ||
      'utm_smart_manager',
  };
}

function summarizePayloadForLogs(payload) {
  if (!isPlainObject(payload)) {
    return null;
  }

  return {
    organizationId: payload.organizationId || null,
    formId: payload.formId || null,
    provider: payload.provider || null,
    mode: payload.mode || null,
    submissionDocId: payload.submissionDocId || null,

    hasConfig: isPlainObject(payload.config),
    hasPolicy: isPlainObject(payload.policy),
    hasInitialPolicy: isPlainObject(payload.initialPolicy),
    hasDestination: isPlainObject(payload.destination),

    destinationProjectId: payload.destination?.projectId || null,
    destinationDatasetId: payload.destination?.datasetId || null,
    includeProviderConfig: payload.includeProviderConfig === true,
    hasCursor: isPlainObject(payload.cursor),

    eventIdCount: Array.isArray(payload.eventIds)
      ? payload.eventIds.length
      : null,
    submissionIdCount: Array.isArray(payload.submissionIds)
      ? payload.submissionIds.length
      : null,
    staleAfterMinutes: Number.isFinite(Number(payload.staleAfterMinutes))
      ? Number(payload.staleAfterMinutes)
      : null,
    forceRefresh: payload.forceRefresh === true,
    verifiedWarehouseRows: payload.verifiedWarehouseRows === true,
  };
}

function summarizeResponseForLogs(data) {
  if (!isPlainObject(data)) {
    return null;
  }

  const integration = data.integration || {};
  const config = data.config || {};
  const health = data.health || {};
  const result = data.result || {};
  const validation = data.validation || integration.validation || {};

  return {
    ok: data.ok === true,

    hasIntegration: Boolean(data.integration),
    hasConfig: Boolean(data.config),
    hasHealth: Boolean(data.health),
    hasResult: Boolean(data.result),
    hasValidation: Boolean(data.validation || integration.validation),

    status: integration.status || config.status || null,
    connectionMode: integration.connectionMode || null,
    validationStatus: validation.status || null,
    isConnected: integration.isConnected === true,

    destinationProjectId:
      integration.destination?.destinationProjectId ||
      integration.destination?.projectId ||
      data.validation?.destination?.projectId ||
      null,
    destinationDatasetId:
      integration.destination?.datasetId ||
      data.validation?.destination?.datasetId ||
      null,
    formCount: Array.isArray(data.forms) ? data.forms.length : null,
    hasNextCursor: Boolean(data.nextCursor),

    countsByStatus: health.countsByStatus || null,
    repairedCount: result.repairedCount ?? null,
    skippedCount: result.skippedCount ?? null,
  };
}

/**
 * Org-level integration status.
 *
 * Generic status reader used by the gallery/card layer. Provider-specific
 * detail modals can use more specific fetchers when they need richer
 * lifecycle details.
 */
export async function fetchIntegrationStatus({
  organizationId,
  provider,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.GET_INTEGRATION_STATUS,
    {
      organizationId: safeOrganizationId,
      provider: safeProvider,
    },
    {
      logLabel: 'fetchIntegrationStatus',
    },
  );

  return data?.integration || null;
}

/**
 * Org-level disconnect.
 *
 * Currently used by Bitly. BigQuery has a dedicated policy-aware disable
 * lifecycle below because BigQuery is a warehouse destination with queue,
 * delivery, validation, and future BI dependencies.
 */
export async function disconnectOrganizationIntegration({
  organizationId,
  provider,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.DISCONNECT_INTEGRATION,
    {
      organizationId: safeOrganizationId,
      provider: safeProvider,
    },
    {
      logLabel: 'disconnectOrganizationIntegration',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * Bitly OAuth start.
 *
 * Bitly remains on its existing OAuth path. This function intentionally does
 * not use the BigQuery provider queue model.
 */
export async function startBitlyConnection({
  organizationId,
  returnUrl,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.START_BITLY_OAUTH,
    {
      organizationId: safeOrganizationId,
      returnUrl: normalizeOptionalString(returnUrl),
    },
    {
      logLabel: 'startBitlyConnection',
    },
  );

  return data || null;
}

/**
 * Bitly click metric sync.
 *
 * Enterprise rule:
 * - The frontend never calls Bitly directly.
 * - The submissions table sends only visible submission ids.
 * - The backend skips fresh rows, calls Bitly for stale/missing metrics,
 *   writes cached provider metrics to formSubmissions, and optionally queues
 *   provider metric snapshots for BigQuery.
 * - The submissions table renders cached values returned from Firestore/backend.
 */
export async function syncBitlySubmissionClicks({
  organizationId,
  formId,
  submissionIds,
  staleAfterMinutes = 30,
  limit = 25,
  forceRefresh = false,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeSubmissionIds = normalizeSubmissionIds(submissionIds);

  if (safeSubmissionIds.length === 0) {
    return {
      ok: true,
      provider: PROVIDERS.BITLY,
      organizationId: safeOrganizationId,
      formId: safeFormId,
      requestedCount: 0,
      syncedCount: 0,
      skippedCount: 0,
      errorCount: 0,
      updates: [],
      skipped: [],
    };
  }

  const parsedLimit = Number(limit);
  const safeLimit =
    Number.isFinite(parsedLimit) && parsedLimit > 0
      ? Math.min(Math.floor(parsedLimit), 50)
      : 25;

  const parsedStaleAfterMinutes = Number(staleAfterMinutes);
  const safeStaleAfterMinutes =
    Number.isFinite(parsedStaleAfterMinutes) &&
    parsedStaleAfterMinutes > 0
      ? Math.min(Math.floor(parsedStaleAfterMinutes), 1440)
      : 30;

  const data = await callIntegrationFunction(
    CALLABLES.SYNC_BITLY_SUBMISSION_CLICKS,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      submissionIds: safeSubmissionIds.slice(0, safeLimit),
      staleAfterMinutes: safeStaleAfterMinutes,
      limit: safeLimit,
      forceRefresh: forceRefresh === true,
    },
    {
      logLabel: 'syncBitlySubmissionClicks',
      logLevel: 'log',
    },
  );

  return data || null;
}

/**
 * Integration form index.
 *
 * Enterprise rule:
 * - The frontend does not query forms directly for integration modals.
 * - The backend returns sanitized, provider-aware form summaries.
 * - BigQuery uses this for its Forms tab; future integrations can reuse it.
 */
export async function listIntegrationForms({
  organizationId,
  provider,
  pageSize = DEFAULT_INTEGRATION_FORMS_PAGE_SIZE,
  cursor = null,
  includeProviderConfig = true,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.LIST_INTEGRATION_FORMS,
    {
      organizationId: safeOrganizationId,
      provider: safeProvider,
      pageSize: normalizeIntegrationFormsPageSize(pageSize),
      cursor: isPlainObject(cursor) ? cursor : null,
      includeProviderConfig: includeProviderConfig !== false,
    },
    {
      logLabel: 'listIntegrationForms',
      logLevel: 'log',
    },
  );

  return data || null;
}

/**
 * BigQuery org-level lifecycle fetch.
 *
 * Use this in the BigQuery detail modal instead of the generic
 * fetchIntegrationStatus() call when you need:
 * - connectionMode
 * - destination
 * - connectionDraft
 * - writerIdentity
 * - validation
 * - exportPolicy / policyDraft
 */
export async function fetchBigQueryOrgIntegration({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.GET_BIGQUERY_ORG_INTEGRATION,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'fetchBigQueryOrgIntegration',
      logLevel: 'log',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery org-level install/setup initialization.
 *
 * This does not "log in" with OAuth and does not publish a destination.
 * It initializes the customer-owned warehouse setup flow and returns the
 * writer identity the customer must grant access to.
 */
export async function installBigQueryOrgIntegration({
  organizationId,
  initialPolicy = null,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const payload = {
    organizationId: safeOrganizationId,
  };

  if (initialPolicy) {
    payload.initialPolicy = requirePlainObject(
      initialPolicy,
      'initialPolicy',
    );
  }

  const data = await callIntegrationFunction(
    CALLABLES.INSTALL_BIGQUERY_ORG_INTEGRATION,
    payload,
    {
      logLabel: 'installBigQueryOrgIntegration',
      logLevel: 'log',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery customer-owned destination draft save.
 *
 * Enterprise rule:
 * - The frontend captures project/dataset/location/tablePrefix.
 * - The backend validates identifiers and stores the draft.
 * - Saving a draft does not publish or switch the active destination.
 */
export async function saveBigQueryConnectionDraft({
  organizationId,
  destination,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeDestination = normalizeBigQueryDestination(destination);

  const data = await callIntegrationFunction(
    CALLABLES.SAVE_BIGQUERY_CONNECTION_DRAFT,
    {
      organizationId: safeOrganizationId,
      destination: safeDestination,
    },
    {
      logLabel: 'saveBigQueryConnectionDraft',
      logLevel: 'log',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery customer-owned destination validation.
 *
 * Enterprise rule:
 * - Backend validates that the runtime writer identity can access/write to
 *   the selected customer-owned dataset.
 * - Validation result is stored against the draft.
 * - This does not publish the destination.
 */
export async function validateBigQueryConnectionDraft({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.VALIDATE_BIGQUERY_CONNECTION_DRAFT,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'validateBigQueryConnectionDraft',
      logLevel: 'warn',
    },
  );

  return data || null;
}

/**
 * BigQuery customer-owned destination publish.
 *
 * Enterprise rule:
 * - Backend requires a validated draft.
 * - Publishing switches the active destination.
 * - Existing queue/history remains intact.
 */
export async function publishBigQueryConnectionDraft({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.PUBLISH_BIGQUERY_CONNECTION_DRAFT,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'publishBigQueryConnectionDraft',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery customer-owned destination draft discard.
 *
 * Enterprise rule:
 * - Discarding a draft does not disconnect an existing published destination.
 * - It only removes the in-progress draft.
 */
export async function discardBigQueryConnectionDraft({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.DISCARD_BIGQUERY_CONNECTION_DRAFT,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'discardBigQueryConnectionDraft',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery org-level disable.
 *
 * Enterprise rule:
 * - Disable stops future BigQuery exports.
 * - It does not delete queue history.
 * - It does not delete customer BigQuery tables/data.
 * - Reconnect uses the connection draft/validate/publish flow.
 */
export async function disableBigQueryOrgIntegration({
  organizationId,
  reason = null,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.DISABLE_BIGQUERY_ORG_INTEGRATION,
    {
      organizationId: safeOrganizationId,
      reason: normalizeOptionalString(reason),
    },
    {
      logLabel: 'disableBigQueryOrgIntegration',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery org-level policy draft save.
 *
 * Enterprise rule:
 * - Policy is backend-validated.
 * - Saving a policy draft does not immediately affect export behavior.
 * - Publishing the draft is a separate action.
 */
export async function saveBigQueryOrgPolicyDraft({
  organizationId,
  policy,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safePolicy = requirePlainObject(policy, 'policy');

  const data = await callIntegrationFunction(
    CALLABLES.SAVE_BIGQUERY_ORG_POLICY_DRAFT,
    {
      organizationId: safeOrganizationId,
      policy: safePolicy,
    },
    {
      logLabel: 'saveBigQueryOrgPolicyDraft',
      logLevel: 'log',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery org-level policy draft publish.
 *
 * Enterprise rule:
 * - Backend publishes the last saved policy draft.
 * - Real export behavior remains protected by dryRun and validated
 *   destination checks.
 */
export async function publishBigQueryOrgPolicyDraft({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.PUBLISH_BIGQUERY_ORG_POLICY_DRAFT,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'publishBigQueryOrgPolicyDraft',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * BigQuery org-level policy draft discard.
 */
export async function discardBigQueryOrgPolicyDraft({
  organizationId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.DISCARD_BIGQUERY_ORG_POLICY_DRAFT,
    {
      organizationId: safeOrganizationId,
    },
    {
      logLabel: 'discardBigQueryOrgPolicyDraft',
      logLevel: 'warn',
    },
  );

  return data?.integration || null;
}

/**
 * Generic form-level integration config fetch.
 *
 * Currently used by Bitly. BigQuery has dedicated wrappers below because
 * BigQuery is an org-level data destination with form-level export policy.
 */
export async function fetchFormIntegrationConfig({
  organizationId,
  formId,
  provider,
  mode = 'auto',
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.GET_FORM_INTEGRATION_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      provider: safeProvider,
      mode: normalizeMode(mode),
    },
    {
      logLabel: 'fetchFormIntegrationConfig',
    },
  );

  return data?.config || null;
}

/**
 * Generic form-level integration config save.
 */
export async function saveFormIntegrationConfig({
  organizationId,
  formId,
  provider,
  config,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeProvider = requireProvider(provider);
  const safeConfig = requirePlainObject(config, 'config');

  const data = await callIntegrationFunction(
    CALLABLES.SAVE_FORM_INTEGRATION_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      provider: safeProvider,
      config: safeConfig,
    },
    {
      logLabel: 'saveFormIntegrationConfig',
    },
  );

  return data?.config || null;
}

/**
 * Generic form-level integration config publish.
 */
export async function publishFormIntegrationConfig({
  organizationId,
  formId,
  provider,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.PUBLISH_FORM_INTEGRATION_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      provider: safeProvider,
    },
    {
      logLabel: 'publishFormIntegrationConfig',
      logLevel: 'log',
    },
  );

  return data?.config || null;
}

/**
 * Generic form-level integration draft discard.
 */
export async function discardFormIntegrationDraft({
  organizationId,
  formId,
  provider,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeProvider = requireProvider(provider);

  const data = await callIntegrationFunction(
    CALLABLES.DISCARD_FORM_INTEGRATION_DRAFT,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      provider: safeProvider,
    },
    {
      logLabel: 'discardFormIntegrationDraft',
      logLevel: 'warn',
    },
  );

  return data?.config || null;
}

/**
 * BigQuery form-level config fetch.
 *
 * BigQuery is intentionally not routed through the generic Bitly-style helper
 * because its backend resolver combines:
 * - org-level BigQuery policy
 * - form-level export override
 * - published form status
 */
export async function fetchBigQueryFormConfig({
  organizationId,
  formId,
  mode = 'auto',
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');

  const data = await callIntegrationFunction(
    CALLABLES.GET_BIGQUERY_FORM_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      mode: normalizeMode(mode),
    },
    {
      logLabel: 'fetchBigQueryFormConfig',
    },
  );

  return data?.config || null;
}

/**
 * BigQuery form-level draft save.
 */
export async function saveBigQueryFormConfig({
  organizationId,
  formId,
  config,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');
  const safeConfig = requirePlainObject(config, 'config');

  const data = await callIntegrationFunction(
    CALLABLES.SAVE_BIGQUERY_FORM_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
      config: safeConfig,
    },
    {
      logLabel: 'saveBigQueryFormConfig',
    },
  );

  return data?.config || null;
}

/**
 * BigQuery form-level publish.
 */
export async function publishBigQueryFormConfig({
  organizationId,
  formId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');

  const data = await callIntegrationFunction(
    CALLABLES.PUBLISH_BIGQUERY_FORM_CONFIG,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
    },
    {
      logLabel: 'publishBigQueryFormConfig',
      logLevel: 'log',
    },
  );

  return data?.config || null;
}

/**
 * BigQuery form-level draft discard.
 */
export async function discardBigQueryFormDraft({
  organizationId,
  formId,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeFormId = requireString(formId, 'formId');

  const data = await callIntegrationFunction(
    CALLABLES.DISCARD_BIGQUERY_FORM_DRAFT,
    {
      organizationId: safeOrganizationId,
      formId: safeFormId,
    },
    {
      logLabel: 'discardBigQueryFormDraft',
      logLevel: 'warn',
    },
  );

  return data?.config || null;
}

/**
 * Step 7D.2: BigQuery export queue health.
 *
 * Read-only diagnostics. This does not repair, requeue, export, or mutate
 * records. It is protected by the backend org integration admin guard.
 */
export async function getBigQueryExportQueueHealth({
  organizationId,
  formId = null,
  submissionDocId = null,
  limit = DEFAULT_QUEUE_HEALTH_LIMIT,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const data = await callIntegrationFunction(
    CALLABLES.GET_BIGQUERY_EXPORT_QUEUE_HEALTH,
    {
      organizationId: safeOrganizationId,
      formId: normalizeOptionalString(formId),
      submissionDocId: normalizeOptionalString(submissionDocId),
      limit: normalizeQueueHealthLimit(limit),
    },
    {
      logLabel: 'getBigQueryExportQueueHealth',
      logLevel: 'log',
    },
  );

  return data || null;
}

/**
 * Alias using the same fetch* naming convention as the rest of this file.
 */
export async function fetchBigQueryExportQueueHealth(args) {
  return getBigQueryExportQueueHealth(args);
}

/**
 * Step 7D.2: Explicit BigQuery queue repair.
 *
 * Enterprise guardrails:
 * - Requires explicit eventIds.
 * - Requires repairReason.
 * - Requires verifiedWarehouseRows === true.
 * - Backend only repairs ERROR / PROCESSING records.
 * - This function never re-exports rows into BigQuery.
 */
export async function repairBigQueryExportQueueRecords({
  organizationId,
  eventIds,
  repairReason,
  verifiedWarehouseRows = false,
}) {
  const safeOrganizationId = requireString(
    organizationId,
    'organizationId',
  );

  const safeEventIds = normalizeEventIds(eventIds);
  const safeRepairReason = requireString(repairReason, 'repairReason');

  if (safeEventIds.length === 0) {
    throw new Error(
      '[integrationFunctions] eventIds must include at least one ID.',
    );
  }

  if (safeEventIds.length > MAX_REPAIR_EVENT_IDS) {
    throw new Error(
      `[integrationFunctions] eventIds cannot exceed ` +
        `${MAX_REPAIR_EVENT_IDS} records per repair request.`,
    );
  }

  if (verifiedWarehouseRows !== true) {
    throw new Error(
      '[integrationFunctions] verifiedWarehouseRows must be true before ' +
        'repairing queue records.',
    );
  }

  const data = await callIntegrationFunction(
    CALLABLES.REPAIR_BIGQUERY_EXPORT_QUEUE_RECORDS,
    {
      organizationId: safeOrganizationId,
      eventIds: safeEventIds,
      repairReason: safeRepairReason,
      verifiedWarehouseRows: true,
    },
    {
      logLabel: 'repairBigQueryExportQueueRecords',
      logLevel: 'warn',
    },
  );

  return data || null;
}

export {
  PROVIDERS,
};
